<template>
  <div>
    <HeaderComponent :isAdminPage="true" />
  </div>
  <div v-if="isInitError" id="com_form_contet_admin">
    <div class="errorform">
      <!-- エラーメッセージの表示エリア -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
  <div v-else class="com_form_contet_admin">
    <section class="com_sec1">
      <div class="inner1">
        <h2 class="title_left">グローバルナビ管理</h2>
      </div>
      <!-- エラーメッセージの表示エリア -->
      <div
        v-if="errorMessage"
        class="error-message"
        style="white-space: pre-wrap"
      >
        {{ errorMessage }}
      </div>
      <!-- グローバルナビ管理一覧 -->
      <div class="">
        <table class="table table-bordered">
          <thead class="thead-light">
            <tr>
              <th class="col_selection">削除</th>
              <th class="col_kanriId">管理ID</th>
              <th class="col_settingName">設定名</th>
              <th class="col_settingTarget">設定対象</th>
              <th class="col_settingTargetDetail">設定対象詳細</th>
              <th class="col_ReflectionDate">反映日</th>
              <th class="col_scheduleDate">反映予定日</th>
              <th class="col_datail">詳細</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in glovalNaviKanriList"
              :key="item.glovalNaviKanriId"
            >
              <td class="col_selection" data-name="mSelection">
                <input
                  id="mSelection"
                  type="checkbox"
                  class="form-check-input"
                  v-model="item.selection"
                />
              </td>
              <td style="text-align: center; padding-left: 10px">
                {{ item.glovalNaviKanriId }}
              </td>
              <td style="text-align: left; padding-left: 10px">
                {{ item.settingName }}
              </td>
              <td>{{ item.settingTargetKbn }}</td>
              <td style="text-align: left; padding-left: 10px">
                {{ item.settingTargetName }}
              </td>
              <td>{{ item.reflectionDate }}</td>
              <td>{{ item.scheduledDate }}</td>
              <td>
                <button
                  type="button"
                  name="mBtnUpdate"
                  class="btn btn-primary btn-sm"
                  @click="showDetail(item.glovalNaviKanriId)"
                >
                  詳細
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="form-inline flex-container">
          <div class="form-group">
            <button
              type="button"
              name="btnDelete"
              class="btn btn-footer btn-primary"
              @click="onDelete"
            >
              削除
            </button>
          </div>

          <div class="form-group">
            <button
              type="button"
              name="btnBack"
              class="btn btn-footer btn-primary"
              @click="onBack"
            >
              戻る
            </button>
            <button
              type="button"
              name="btnClear"
              class="btn btn-footer btn-primary"
              @click="onClear"
            >
              クリア
            </button>
            <button
              type="button"
              name="btnAdd"
              class="btn btn-footer btn-primary"
              @click="showAdd"
            >
              新規登録
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
  <div>
    <!-- フッタ -->
    <FooterComponent />
  </div>
  <BackToTop />
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import HeaderComponent from "../layout/HeaderComponent.vue";
import FooterComponent from "../layout/FooterComponent.vue";
import { formatMessage, isTokenAlive, commonHandleAxiosError } from "@/Common";
import { AppConstants } from "@/constants/AppConstants";
import apiAuthClient from "@/util/apiClientAuth";
import { useProcessingStore } from "../../store/useProcessingStore";
import messages from "@/assets/messages.json";
import BackToTop from "../controls/BackToTop.vue";
import { useUserStore } from "@/store/userStore";
import { useRouter } from "vue-router";

interface GlovalNaviKanriList {
  selection: boolean;
  glovalNaviKanriId: string;
  settingName: string;
  settingTargetKbn: string;
  settingTargetName: string;
  reflectionDate: string;
  scheduledDate: string;
}

interface GlonaviData {
  glonaviKanriId: string;
}

export default defineComponent({
  name: "GlobalNaviManegmentListComponent",
  components: {
    HeaderComponent,
    FooterComponent,
    BackToTop,
  },
  setup() {
    const isInitError = ref(true); // 初期表示時のエラーフラグ
    const processingStore = useProcessingStore();
    const isDataFetched = ref(false); // データが取得済みかどうかを追跡
    const errorMessage = ref(""); // エラーメッセージ（キーワード検索の下のやつ）
    const isKaunetAdmin = ref(false); // カウネット管理者フラグ
    const router = useRouter();
    const { logoutUser } = useUserStore();
    const glovalNaviKanriList = ref<GlovalNaviKanriList[]>([]);
    const agCode = ref(""); // ローカルストレージ．AGコード
    const userId = ref(""); // ローカルストレージ．ユーザーID

    // 初期処理
    onMounted(async () => {
      // ローカルストレージからユーザー情報を取得して内部変数に展開
      const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);
      if (userInfo) {
        const parsedUserInfo = JSON.parse(userInfo);
        agCode.value = parsedUserInfo.agCode;
        userId.value = parsedUserInfo.userId;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      // 初期処理時はTrue
      isInitError.value = true;
      isDataFetched.value = false;

      try {
        // グローバルナビ管理データ一覧取得
        if (!(await getGlonaviKanriList())) {
          return;
        }

        isInitError.value = false;
        isDataFetched.value = true; // データが取得されたことを設定
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    });

    /**
     * グローバルナビ管理データ一覧取得
     */
    const getGlonaviKanriList = async (): Promise<boolean> => {
      try {
        // グローバルナビ一覧取得API
        const response = await apiAuthClient.post(
          "/api/GlovalNaviManegment/getKanriDataList",
          {
            systemKbn: AppConstants.SYSTEM_KBN_WITH,
            agCode: agCode.value,
            userId: userId.value,
          }
        );
        // レスポンスを保持
        glovalNaviKanriList.value = response.data.glovalNaviKanriList;

        return true;
      } catch (error: unknown) {
        // エラー処理
        handleAxiosError(error);
        return false;
      }
    };

    /**
     * 詳細ボタン押下
     * @param glovalKanriId グローバルナビ管理ID
     */
    const showDetail = (glovalKanriId: string) => {
      if (processingStore.isProcessing) {
        // 何か処理中なら処理をしない
        return;
      }

      // 対象行取得
      const target = getGlonaviData(glovalKanriId);
      if (!target) {
        return;
      }

      // 登録画面へ遷移
      router.push({
        path: "/glovalnavi-kanri",
        query: {
          glovalNaviKanriId: target.glovalNaviKanriId,
          editMode: AppConstants.CONTENTS_EDIT_MODE_UPDATE,
        },
      });
    };

    /**
     * グローバルナビ管理IDに該当するグローバルナビ管理データを返却します。
     * @param glovalKanriId グローバルナビ管理ID
     */
    const getGlonaviData = (
      glovalKanriId: string
    ): GlovalNaviKanriList | null => {
      const glonaviData = glovalNaviKanriList.value.find(
        (glonaviData) => glonaviData?.glovalNaviKanriId == glovalKanriId
      );
      return glonaviData ? glonaviData : null;
    };

    /**
     * 削除ボタン押下
     */
    const onDelete = async () => {
      if (processingStore.isProcessing) {
        return;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      // メッセージクリア
      errorMessage.value = "";

      try {
        // 削除対象絞り込み
        const targetList: GlonaviData[] = glovalNaviKanriList.value
          .filter((glonavi) => glonavi.selection)
          .map((glonavi) => ({
            glonaviKanriId: glonavi.glovalNaviKanriId,
          }));

        // 選択件数チェック
        if (targetList.length < 1) {
          errorMessage.value = formatMessage(messages.ERR_0003, "明細");
          return;
        }

        // 確認ダイアログ表示
        const isConfirm = window.confirm(formatMessage(messages.CNF_0004));
        if (!isConfirm) {
          // いいえの場合
          return;
        }

        // グローバルナビ管理データ削除
        try {
          // トークン有効性チェック
          if ((await isTokenAlive()) == false) {
            // トークンが無効な場合はログイン画面へ
            logoutUser();
            alert(messages.ERR_USER001);
            router.push("/login");
            return;
          }

          // グローバルナビ管理データ削除API
          await apiAuthClient.post("/api/GlovalNaviManegment/deleteKanriData", {
            systemKbn: AppConstants.SYSTEM_KBN_WITH,
            agCode: agCode.value,
            userId: userId.value,
            gloNaviKanriIdList: targetList,
          });

          // 完了メッセージ表示
          alert(formatMessage(messages.INF_0001, "データ", "削除"));

          // グローバルナビ管理データ一覧取得
          await getGlonaviKanriList();
        } catch (error: unknown) {
          // エラー処理
          handleAxiosError(error);
        }
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    };

    /**
     * 戻るボタン押下
     */
    const onBack = () => {
      if (processingStore.isProcessing) {
        // 何か処理中なら処理をしない
        return;
      }

      // 管理者トップへ遷移
      router.push("/kanrisya-top");
    };

    /**
     * クリアボタン押下
     */
    const onClear = async () => {
      if (processingStore.isProcessing) {
        return;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      // メッセージクリア
      errorMessage.value = "";

      try {
        // トークン有効性チェック
        if ((await isTokenAlive()) == false) {
          // トークンが無効な場合はログイン画面へ
          logoutUser();
          alert(messages.ERR_USER001);
          router.push("/login");
          return;
        }

        // グローバルナビ管理一覧情報取得
        await getGlonaviKanriList();
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    };

    /**
     * 新規登録ボタン押下
     */
    const showAdd = () => {
      if (processingStore.isProcessing) {
        // 何か処理中なら処理をしない
        return;
      }

      // 登録画面へ遷移
      router.push("/glovalnavi-kanri");
    };

    /**
     * AxiosErrorを処理します。
     * @param error エラー情報
     */
    const handleAxiosError = (error: unknown) => {
      errorMessage.value = commonHandleAxiosError(error, true);
    };

    return {
      isInitError,
      isDataFetched,
      errorMessage,
      isKaunetAdmin,
      router,
      showDetail,
      onDelete,
      onBack,
      onClear,
      showAdd,
      glovalNaviKanriList,
      getGlonaviKanriList,
    };
  },
});
</script>

<style scoped>
td {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 0;
}
.col_selection {
  width: 4%;
}
.col_kanriId {
  width: 10%;
}
.col_settingName {
  width: 22%;
}
.col_settingTarget {
  width: 12%;
}
.col_settingTargetDetail {
  width: 22%;
}
.col_ReflectionDate {
  width: 12%;
}
.col_scheduleDate {
  width: 12%;
}
.col_datail {
  width: 6%;
}
.changed {
  background-color: #bed0f3;
}
.unchanged {
  background-color: #fffdbf;
}
.errorform {
  margin-top: 50px;
  width: 100%;
  height: 500px;
  text-align: center;
}
.flex-container {
  display: flex;
  justify-content: space-between;
}
.btn {
  margin: 0.25rem 0.125rem;
}
.btn-footer {
  margin: 0.5rem 0.55rem;
  padding: 0.375rem 2rem;
  border-radius: 1rem;
  width: 140px;
}
.table > :not(caption) > * > * {
  padding: 0rem 0.2rem;
}
thead {
  text-align: center;
  vertical-align: middle;
}
.thead-light {
  background-color: lightgray;
}
tbody {
  text-align: center;
  vertical-align: middle;
}
</style>
